import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError, setAuthToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const [error, setError] = useState("");

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const finish = (data) => {
    if (data.access_token) setAuthToken(data.access_token);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
  };

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.two_factor_required) return data;
      finish(data);
      return true;
    } catch (e) {
      setError(apiError(e, "Invalid credentials"));
      return false;
    }
  };

  const verifyOtp = async (challenge_id, code) => {
    setError("");
    try {
      const { data } = await api.post("/auth/verify-otp", { challenge_id, code });
      finish(data);
      return true;
    } catch (e) {
      setError(apiError(e, "Invalid code"));
      return false;
    }
  };

  const resendOtp = async (challenge_id) => {
    setError("");
    try {
      await api.post("/auth/resend-otp", { challenge_id });
      return true;
    } catch (e) {
      setError(apiError(e, "Could not resend code"));
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setAuthToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, verifyOtp, resendOtp, logout, error, setError, isAdmin: user?.role === "admin" }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
